// CRM Layout - Layout con Sidebar para páginas del CRM
import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { CRMSidebar } from './CRMSidebar';
import { CRMHeader } from './CRMHeader';
import { cn } from '@/lib/utils';
import { AnimatePresence } from 'framer-motion';
import { FreeDialerWidget } from '@/components/workSession';
import { CallSidebar } from '@/components/CRM/CallSidebar';
import { useCallStore } from '@/stores/useCallStore';
import { useNotificationStream } from '@/hooks/useNotificationStream';

const SIDEBAR_COLLAPSED_KEY = 'crm_sidebar_collapsed';

export function CRMLayout() {
  // Subscribe once per CRM session so the bell + dropdown update in
  // realtime via the backend's SSE stream. The hook is a pure side effect
  // (returns void). See docs/activity-center/ADR_AC_001 (migrofast).
  useNotificationStream();

  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const callSidebarOpen = useCallStore((s) => s.isOpen);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return saved === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isSidebarCollapsed));
    } catch (e) {
      console.warn('Error saving sidebar state:', e);
    }
  }, [isSidebarCollapsed]);

  // FM-06: Cerrar sidebar móvil al navegar (evitar overlay visible tras cambiar de sección)
  // + Scroll-to-top al cambiar de ruta para evitar que páginas largas arranquen fuera de viewport
  useEffect(() => {
    setIsSidebarOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Cerrar drawer mobile con tecla ESC + bloquear scroll del body mientras esté abierto.
  useEffect(() => {
    if (!isSidebarOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsSidebarOpen(false);
    };
    document.addEventListener('keydown', onEsc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [isSidebarOpen]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar Desktop - Visible en pantallas >= 1024px (lg) */}
      <div className={cn(
        "hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 z-40 transition-all duration-300",
        isSidebarCollapsed ? "lg:w-16" : "lg:w-64"
      )}>
        <CRMSidebar isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} />
      </div>

      {/* Mobile Sidebar Drawer — slide-in desde la izquierda en <lg.
          Inline (no shared Drawer) para evitar padding interno extra que
          interfiere con el padding propio del CRMSidebar. */}
      {isSidebarOpen && (
        <div className="lg:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
            onClick={() => setIsSidebarOpen(false)}
            aria-hidden="true"
          />
          {/* Panel */}
          <div
            className="fixed top-0 left-0 bottom-0 z-[110] w-72 max-w-[85vw] bg-white dark:bg-gray-900 shadow-xl flex flex-col animate-in slide-in-from-left duration-200"
            role="dialog"
            aria-modal="true"
            aria-label="Menú de navegación CRM"
          >
            <CRMSidebar onClose={() => setIsSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Marcador libre Telnyx WebRTC — flotante en todo el CRM */}
      <FreeDialerWidget />

      {/* Call Sidebar — panel lateral de llamada con cuestionario interactivo */}
      <AnimatePresence>
        {callSidebarOpen && <CallSidebar />}
      </AnimatePresence>

      {/* Content area con padding para sidebars */}
      <div className={cn(
        "transition-all duration-300",
        isSidebarCollapsed ? "lg:pl-16" : "lg:pl-64",
        callSidebarOpen && "sm:pr-[380px] lg:pr-[400px]",
      )}>
        <CRMHeader onMenuClick={() => setIsSidebarOpen(true)} />

        {/* Main Content - key por ruta para forzar re-render al cambiar (p. ej. expedientes) */}
        <main>
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3 sm:py-4 md:py-6">
            <Outlet key={location.pathname} />
          </div>
        </main>
      </div>
    </div>
  );
}
